import { useTranslation } from "react-i18next";
import { useState, useMemo, useCallback } from "react";
import { Layout } from "@/components/layout";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { toastApiError } from "@/lib/error-messages";
import { useFuzzySearch } from "@/hooks/use-fuzzy-search";
import { Plus, Phone, Mail, User, Search, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { CustomerForm } from "@/components/customers/CustomerForm";
import { useLocation } from "wouter";
import { getCustomerDisplayName } from "@shared/customer-utils";

import { getCustomers } from "@/requests/customers/get-customers";
import { useQuery } from "@tanstack/react-query";
import { useCreateCustomer } from "@/requests/customers/create-customer";
import { type InsertCustomer } from "@shared/schema";
export default function Customers() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const { data: customers, isLoading } = useQuery(getCustomers());
  const { mutate: createCustomer, isPending: isCreating } = useCreateCustomer();

  const onSubmit = (data: InsertCustomer) => {
    createCustomer(data, {
      onSuccess: () => {
        setOpen(false);
        toast({
          title: t("pages.customers.customerCreatedSuccessfully"),
        });
      },
      onError: (error) => {
        toastApiError(toast, error, t("pages.customers.failedToCreateCustomer"));
      },
    });
  };

  const handleCancel = () => {
    setOpen(false);
  };

  // Enhanced customers with full names for better search
  const customersWithFullNames = useMemo(() => {
    if (!customers) return [];

    return customers.map((customer: any) => ({
      ...customer,
      fullName: getCustomerDisplayName(customer),
    }));
  }, [customers]);

  // Fuzzy search for customers
  const filteredCustomers = useFuzzySearch({
    items: customersWithFullNames,
    searchQuery,
    keys: [
      "fullName",
      "firstName",
      "lastName",
      "businessName",
      "email",
      "phone",
    ],
  });

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleViewQuotes = useCallback(
    (customer: any) => {
      const customerFullName = getCustomerDisplayName(customer);
      navigate(`/quotes?search=${encodeURIComponent(customerFullName)}`);
    },
    [navigate],
  );

  return (
    <Layout headerContent={<span className="font-semibold">{t("pages.customers.customers")}</span>}>
      <div className="space-y-6">
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="lg" className="h-12 text-base">
                <Plus className="h-5 w-5 mr-2" />
                {t("pages.customers.addCustomer")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t("pages.customers.addNewCustomer")}</DialogTitle>
              </DialogHeader>
              <CustomerForm
                onSubmit={onSubmit}
                onCancel={handleCancel}
                isSubmitting={isCreating}
              />
            </DialogContent>
          </Dialog>
        </div>

        {/* Search Input */}
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder={t("pages.customers.searchCustomersByName")}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10 w-full h-12 text-base"
          />
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-card p-6 rounded-lg border animate-pulse"
              >
                <div className="h-6 bg-muted rounded mb-3"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
                <div className="h-10 bg-muted rounded mt-4"></div>
              </div>
            ))}
          </div>
        ) : filteredCustomers && filteredCustomers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCustomers.map((customer: any) => (
              <div
                key={customer.uid}
                className="bg-card p-6 rounded-lg border hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">
                      {getCustomerDisplayName(customer)}
                    </h3>
                    <Badge
                      variant="secondary"
                      className={`text-[10px] px-2 py-0.5 shrink-0 ${
                        customer.customerType === "business"
                          ? "bg-orange-100 text-orange-700 hover:bg-orange-100 hover:text-orange-700"
                          : "bg-blue-100 text-blue-700 hover:bg-blue-100 hover:text-blue-700"
                      }`}
                    >
                      {customer.customerType === "business"
                        ? t("pages.customers.business")
                        : t("pages.customers.individual")}
                    </Badge>
                  </div>

                  <div className="space-y-3 flex-1">
                    {customer.phone && (
                      <div className="flex items-center text-sm">
                        <Phone className="h-5 w-5 mr-3 text-muted-foreground" />
                        <span>{customer.phone}</span>
                      </div>
                    )}
                    {customer.email && (
                      <div className="flex items-center text-sm">
                        <Mail className="h-5 w-5 mr-3 text-muted-foreground" />
                        <span className="truncate">{customer.email}</span>
                      </div>
                    )}
                    {(customer.streetAddress ||
                      customer.city ||
                      customer.state) && (
                      <div className="flex items-start text-sm">
                        <MapPin className="h-5 w-5 mr-3 text-muted-foreground mt-0.5" />
                        <div className="flex-1">
                          {customer.streetAddress && (
                            <div>{customer.streetAddress}</div>
                          )}
                          {customer.streetAddress2 && (
                            <div>{customer.streetAddress2}</div>
                          )}
                          {(customer.city ||
                            customer.state ||
                            customer.zipCode) && (
                            <div>
                              {customer.city && customer.city}
                              {customer.city && customer.state && ", "}
                              {customer.state && customer.state}
                              {customer.zipCode && ` ${customer.zipCode}`}
                            </div>
                          )}
                          {customer.country && customer.country !== "US" && (
                            <div>{customer.country}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    className="w-full mt-4 h-12 text-base"
                    size="lg"
                    variant="outline"
                    onClick={() => handleViewQuotes(customer)}
                  >
                    {t("pages.customers.viewQuotes")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <User className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-medium text-lg mb-2">
                {searchQuery
                  ? t("pages.customers.noCustomersMatchYour")
                  : t("pages.customers.noCustomersFound")}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery
                  ? t("pages.customers.tryAdjustingYourSearch")
                  : t("pages.customers.getStartedByAdding")}
              </p>
              {!searchQuery && (
                <Button onClick={() => setOpen(true)} size="lg">
                  <Plus className="h-5 w-5 mr-2" />
                  {t("pages.customers.addCustomer")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
